import { FOOTER } from "@/constants/testIds";
import { Link } from "react-router-dom";
import { ShieldCheck, Truck, Lock, HeartHandshake, Star } from "lucide-react";
import { useLeadModal } from "@/components/shared/LeadProvider";
import ShwcareLegitScript from "@/components/landing/ShwcareLegitScript";

const RED = "#E11D2A";

const trust = [
  { icon: ShieldCheck,     title: "Licensed Providers", sub: "U.S. Licensed & Verified" },
  { icon: Truck,           title: "Discreet Shipping",  sub: "Always Private. Always Fast." },
  { icon: Lock,            title: "Secure & Private",   sub: "Your Privacy is Our Priority" },
  { icon: HeartHandshake,  title: "Ongoing Support",    sub: "We're With You All The Way" },
];

export default function Footer() {
  const { open } = useLeadModal();
  return (
    <footer
      id="footer"
      data-testid={FOOTER.section}
      className="text-white"
      style={{ background: "#081B33" }}
    >
      {/* Trust bar */}
      <div className="border-y border-white/10">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto py-8 md:py-10 grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 items-center">
          {trust.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="flex items-center gap-3" data-testid={`footer-trust-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center shrink-0"
                  style={{ color: RED }}>
                  <Icon size={18} />
                </span>
                <div>
                  <div className="font-extrabold tracking-[0.14em] text-[11px] text-white uppercase">{t.title}</div>
                  <div className="text-[11px] text-white/55 mt-1">{t.sub}</div>
                </div>
              </div>
            );
          })}
          <div className="flex items-center gap-3 col-span-2 md:col-span-1" data-testid="footer-rating">
            <div className="flex items-center gap-0.5" style={{ color: "#F5B400" }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={16} fill="currentColor" />
              ))}
            </div>
            <div>
              <div className="font-heavy text-[18px] text-white leading-none">4.9/5</div>
              <div className="text-[11px] text-white/55 mt-1">Thousands of Happy Patients</div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer columns */}
      <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto py-14 md:py-16 grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-10">
        <div className="col-span-2 md:col-span-4">
          <Link to="/" className="inline-block" data-testid="footer-logo">
            <img
              src="/assets/bestrx-logo-main.png"
              alt="BestRx"
              className="h-12 md:h-14 w-auto"
              draggable={false}
            />
          </Link>
          <p className="mt-5 text-[14px] text-white/65 max-w-sm leading-relaxed">
            Medically guided programs for women and men. Clinician-led, pharmacy-shipped,
            built to help you become a better version of yourself.
          </p>
          <button
            onClick={() => open({ source: "footer" })}
            data-testid="footer-cta-assessment"
            className="mt-6 inline-flex items-center gap-1.5 px-5 py-3 font-heavy text-white text-[12px] tracking-[0.14em] uppercase"
            style={{ background: RED, boxShadow: "0 10px 24px rgba(225,29,42,0.32)" }}
          >
            Start Your Assessment →
          </button>
        </div>

        <div className="md:col-span-2">
          <div className="font-extrabold text-[11px] tracking-[0.18em] text-white/45 mb-4 uppercase">Shop</div>
          <ul className="space-y-3 text-[14px] text-white/75">
            <li><Link to="/category/weight-loss"   className="hover:text-white" data-testid="footer-shop-weight-loss">Weight Loss</Link></li>
            <li><Link to="/category/wellness"      className="hover:text-white" data-testid="footer-shop-wellness">Wellness</Link></li>
            <li><Link to="/mens-garage"            className="hover:text-white" data-testid="footer-shop-mens">Men's Health</Link></li>
            <li><Link to="/womens-shop"            className="hover:text-white" data-testid="footer-shop-womens">Women's Health</Link></li>
            <li><Link to="/#medication-zone"       className="hover:text-white" data-testid="footer-shop-zone">Medication Zone</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="font-extrabold text-[11px] tracking-[0.18em] text-white/45 mb-4 uppercase">Learn</div>
          <ul className="space-y-3 text-[14px] text-white/75">
            <li><Link to="/learn"      className="hover:text-white" data-testid="footer-learn-resources">Resource Center</Link></li>
            <li><Link to="/results"    className="hover:text-white" data-testid="footer-learn-results">Real Results</Link></li>
            <li><Link to="/providers"  className="hover:text-white" data-testid="footer-learn-providers">Our Providers</Link></li>
            <li><Link to="/faq"        className="hover:text-white" data-testid="footer-learn-faq">FAQ</Link></li>
            <li><Link to="/about"      className="hover:text-white" data-testid="footer-learn-about">About BestRx</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="font-extrabold text-[11px] tracking-[0.18em] text-white/45 mb-4 uppercase">Contact</div>
          <ul className="space-y-3 text-[14px] text-white/75">
            <li>
              <a href="tel:+18447031780" className="hover:text-white font-heavy" data-testid="footer-contact-phone" style={{ color: RED }}>
                844-703-1780
              </a>
            </li>
            <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact-page">Contact Us</Link></li>
            <li className="text-white/45 text-[12px]">Mon–Fri 8a–8p CST</li>
            <li className="text-white/45 text-[12px]">Sat 9a–5p CST</li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="font-extrabold text-[11px] tracking-[0.18em] text-white/45 mb-4 uppercase">Compliance</div>
          <ul className="space-y-3 text-[14px] text-white/75">
            <li><Link to="/privacy"     className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/terms"       className="hover:text-white" data-testid="footer-terms">Terms of Service</Link></li>
            <li><Link to="/sms-consent" className="hover:text-white" data-testid="footer-sms">SMS Consent</Link></li>
            <li><Link to="/hipaa"       className="hover:text-white" data-testid="footer-hipaa">HIPAA Notice</Link></li>
            <li className="text-white/45 pt-1 text-[12px]">Licensed in all 50 states</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[12px] text-white/45">
          <p>© {new Date().getFullYear()} BestRx. All rights reserved.</p>
          <ShwcareLegitScript brand="BestRx" />
          <p className="md:text-right max-w-xl">
            BestRx is a telehealth platform connecting patients with independent clinicians.
            Medications are prescribed only when clinically appropriate. Results vary.
          </p>
        </div>
      </div>
    </footer>
  );
}
